import { useState } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { get } from "lodash";
import { useQuery } from "react-query";
import { useDebounce } from "use-debounce";
import { LinkedAutoComplete } from "../ui-form/linked-controls";
import { FormField } from "../ui-form/form-field";
import { renderField } from "../lib/FormConfigs/Renderer";
import type { FieldConfig, SelectFieldConfig } from "../lib/FormConfigs/config";
import {
  customerDeclarationFields,
  destinationDeclarationFields,
  paymentDeclarationFields,
} from "../lib/FormConfigs";
import { modeOf } from "../lib/forex-payload";
import { getCustomers } from "../apis";
import {
  listCustomerDocumentOptions,
  listForexCorporates,
} from "service/moneeflo/legacy-forex-create-api";

export type DeclarationGroup = "customer" | "destination" | "payment";

const GROUPS: Record<DeclarationGroup, FieldConfig[]> = {
  customer: customerDeclarationFields,
  destination: destinationDeclarationFields,
  payment: paymentDeclarationFields,
};

interface Props {
  form: UseFormReturn<any>;
  group: DeclarationGroup;
}

/**
 * The declarations core needs on a forex order, in the wizard's own field
 * layout (the dotted step column beside each field, as on the destination
 * step). Dependent fields appear after the one that triggers them.
 */
export function DeclarationFields({ form, group }: Props) {
  // Subscribed here: the steps are memoised, so a parent's watch() would not
  // re-render this group when the field that triggers a dependent one changes.
  const values = useWatch({ control: form.control }) as Record<string, any>;
  const mode = modeOf(values);
  const customerId: string | undefined = values?.customers?.[0]?.customerId;
  const [travellerTerm, setTravellerTerm] = useState("");
  const [debouncedTerm] = useDebounce(travellerTerm, 600);

  const wantsCompany =
    group === "customer" && mode === "sale" && ["employer", "sponsor"].includes(values?.declarations?.payerKind);
  const { data: companies } = useQuery(["forex-corporates"], listForexCorporates, {
    enabled: wantsCompany,
    staleTime: 60_000,
  });

  const wantsEvidence =
    group === "destination" && mode === "sale" && values?.declarations?.payerKind === "employer" && !!customerId;
  const { data: documents } = useQuery(
    ["forex-customer-documents", customerId],
    () => listCustomerDocumentOptions(customerId as string),
    { enabled: wantsEvidence },
  );

  const wantsTraveller = group === "customer" && values?.declarations?.travellerDifferent === "yes";
  const { data: travellers, isLoading: searching } = useQuery(
    ["forex-traveller", debouncedTerm],
    () => getCustomers(debouncedTerm),
    { enabled: wantsTraveller && debouncedTerm.trim().length >= 4 },
  );

  const fields = GROUPS[group].filter((f) => !f.showIf || f.showIf(values, mode));
  if (fields.length === 0) return null;

  return (
    <div className="max-w-sm mt-2" data-testid={`declarations-${group}`}>
      <div className="grid grid-cols-[auto_1fr] gap-x-[18.5px]">
        {fields.map((field, index) => {
          const isLast = index === fields.length - 1;
          const filled = !!get(values, field.name);
          return (
            <div key={field.name} className="contents">
              <div className="flex flex-col items-center justify-start">
                <div
                  className={`w-[9px] h-[9px] rounded-full border mt-1.5 flex-shrink-0 transition-colors duration-300 ${
                    filled ? "bg-blue-700 border-blue-700" : "bg-white-A700 border-blue-700"
                  }`}
                />
                {!isLast && (
                  <div className={`w-0.5 flex-1 border-l-2 border-dotted min-h-[1.5rem] ${filled ? "border-blue-700 " : "border-gray-300 "}`} />
                )}
              </div>
              <div className="pb-6">
                <FormField
                  form={form}
                  name={field.name}
                  label={field.label}
                  isOptionalField={field.required}
                  render={({ field: formField }) => {
                    if (field.name === "declarations.travellerCustomerId") {
                      return (
                        <LinkedAutoComplete
                          value={{ value: formField.value ?? "", label: travellerTerm }}
                          onValueChange={(sel) => {
                            const found = travellers?.find((c) => c.id === sel?.value);
                            if (found) {
                              formField.onChange(found.id);
                              setTravellerTerm(`${found.full_name} (${found.phone_number})`);
                            }
                          }}
                          options={(travellers ?? []).map((c) => ({
                            value: c.id,
                            label: `${c.full_name} (${c.phone_number})`,
                          }))}
                          inputValue={travellerTerm}
                          onInputChange={(text) => {
                            setTravellerTerm(text);
                            if (formField.value) formField.onChange("");
                          }}
                          emptyMessage="No customers found"
                          isLoading={searching}
                          placeholder={field.placeholder}
                        />
                      );
                    }
                    if (field.type === "select") {
                      const dynamic: Record<string, SelectFieldConfig["options"] | undefined> = {
                        "declarations.payerOrganizationId": companies,
                        "declarations.sponsorEvidenceDocumentId": documents,
                      };
                      const options = dynamic[field.name] ?? field.options;
                      return renderField({ ...field, options } as FieldConfig, formField);
                    }
                    return renderField(field, formField);
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
